// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/MessageAvatar.module.css';
import {useMaybeMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import {PreloadableUserPopout} from '@app/features/channel/components/PreloadableUserPopout';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Avatar} from '@app/features/ui/components/Avatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {AvatarDecorationOverlay} from '@app/features/user/components/AvatarDecorationOverlay';
import type {User} from '@app/features/user/models/User';
import {getAvatarDecorationSizePx} from '@app/features/user/utils/AvatarDecorationCutout';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';
import type React from 'react';

export const MessageAvatar = observer(
	({
		user,
		message,
		guildId,
		size,
		className,
		isHovering,
	}: {
		user: User;
		message: Message;
		guildId?: string;
		size: 16 | 24 | 32 | 40 | 48 | 80 | 120;
		className: string;
		isHovering: boolean;
		isPreview: boolean;
	}) => {
		const onPopoutToggle = useMaybeMessageViewContext()?.onPopoutToggle;
		const handlePopoutOpen = useCallback(() => onPopoutToggle?.(true), [onPopoutToggle]);
		const handlePopoutClose = useCallback(() => onPopoutToggle?.(false), [onPopoutToggle]);
		const decorationStyle = useMemo(
			() =>
				({
					'--avatar-decoration-size': remFromPx(getAvatarDecorationSizePx(size)),
				}) as React.CSSProperties,
			[size],
		);
		const showDecoration = message.webhookId == null;
		return (
			<PreloadableUserPopout
				user={user}
				isWebhook={message.webhookId != null}
				webhookId={message.webhookId ?? undefined}
				guildId={guildId}
				channelId={message.channelId}
				message={message}
				enableLongPressActions={false}
				onPopoutOpen={handlePopoutOpen}
				onPopoutClose={handlePopoutClose}
				data-flx="channel.message-avatar.preloadable-user-popout"
			>
				<FocusRing data-flx="channel.message-avatar.focus-ring">
					<span
						className={clsx(className, styles.frame)}
						style={decorationStyle}
						data-user-id={user.id}
						data-guild-id={guildId}
					>
						<Avatar
							user={user}
							size={size}
							forceAnimate={isHovering}
							guildId={guildId}
							data-flx="channel.message-avatar.avatar"
						/>
						{showDecoration ? (
							<AvatarDecorationOverlay userId={user.id} avatarSize={size} active={isHovering} />
						) : null}
					</span>
				</FocusRing>
			</PreloadableUserPopout>
		);
	},
);
